import React, { useState, useEffect, useRef } from "react";
import TrainingDesktop from "./TrainingDesktop";
import TrainingMobile from "./TrainingMobile";
import TrainingList from "./TrainingList";
import TrainingDetail from "./TrainingDetail";
import TrainingProgress from "./TrainingProgress";
import useIsMobile from "../../../hooks/useIsMobile";
import "../css/Common.css";
import "./css/Training.css";

/**
 * Training - Main container component for the Training feature.
 *
 * Owns all shared state and event handlers, then delegates UI rendering to
 * TrainingDesktop or TrainingMobile based on the current viewport width.
 *
 * Orchestrates navigation between:
 * - Training list (browse all trainings)
 * - Training detail (reading + quiz)
 * - Training progress (dashboard with badges/certificates)
 *
 * Usage:
 * <Training initialView="list" />      // Start with list view
 * <Training initialView="progress" />  // Start with progress dashboard
 *
 * @param {string} initialView - Starting view: "list", "detail", or "progress"
 * @param {function} setHeroContent - Optional callback from Hero.js for navigation
 */
const Training = ({ initialView = "list", setHeroContent }) => {
  const isMobile = useIsMobile();
  const [currentView, setCurrentView] = useState(initialView);
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSearchTerm, setActiveSearchTerm] = useState("");
  const searchInputRef = useRef(null);
  const searchContainerRef = useRef(null);

  // Scroll to top when component mounts
  useEffect(() => {
    // Try to scroll the main-content container first, then fallback to window
    const mainContent = document.querySelector(".main-content");
    if (mainContent) {
      mainContent.scrollTo(0, 0);
    } else {
      window.scrollTo(0, 0);
    }
  }, []);

  // Focus search input when expanded
  useEffect(() => {
    if (searchExpanded && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchExpanded]);

  // Handle click outside to collapse search (only if no active search)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchExpanded &&
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target)
      ) {
        // Only collapse if no active search term
        if (!activeSearchTerm) {
          setSearchQuery("");
          setSearchExpanded(false);
          // Dispatch event to reset trainings to current category filter
          window.dispatchEvent(new CustomEvent("trainingSearchReset"));
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [searchExpanded, activeSearchTerm]);

  // Listen for category selection to collapse search and clear active term
  useEffect(() => {
    const handleCategorySelected = () => {
      setSearchQuery("");
      setActiveSearchTerm("");
      setSearchExpanded(false);
    };

    window.addEventListener("trainingCategorySelected", handleCategorySelected);
    return () =>
      window.removeEventListener(
        "trainingCategorySelected",
        handleCategorySelected,
      );
  }, []);

  const handleSelectTraining = (training) => {
    setSelectedTraining(training);
    setSelectedSession(training.sessionId || null);
    setCurrentView("detail");
  };

  const handleBackToList = () => {
    setSelectedTraining(null);
    setSelectedSession(null);
    setCurrentView("list");
    // Clear search when going back
    setSearchQuery("");
    setActiveSearchTerm("");
    setSearchExpanded(false);
    window.dispatchEvent(new CustomEvent("trainingSearchReset"));
  };

  const handleSearch = () => {
    if (searchQuery.trim()) {
      setActiveSearchTerm(searchQuery.trim());
      // Trigger search in TrainingList
      const event = new CustomEvent("trainingSearch", {
        detail: searchQuery.trim(),
      });
      window.dispatchEvent(event);
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const clearSearch = () => {
    setSearchQuery("");
    setActiveSearchTerm("");
    setSearchExpanded(false);
    window.dispatchEvent(new CustomEvent("trainingSearchReset"));
  };

  const handleTrainingComplete = (result) => {
    console.log("[Training] Training completed:", result);
    // Could show a celebration modal here
    // Then go back to list or progress view
    setCurrentView("progress");
  };

  // Shared props forwarded to whichever shell renders the UI.
  const shellProps = {
    currentView,
    selectedTraining,
    selectedSession,
    searchExpanded,
    searchQuery,
    activeSearchTerm,
    searchInputRef,
    searchContainerRef,
    setCurrentView,
    setSearchQuery,
    setSearchExpanded,
    handleSelectTraining,
    handleBackToList,
    handleSearch,
    handleSearchKeyPress,
    clearSearch,
    handleTrainingComplete,
  };

  return isMobile ? (
    <TrainingMobile {...shellProps} />
  ) : (
    <TrainingDesktop {...shellProps} />
  );
};

export default Training;

// Also export individual components for direct use
export { TrainingList, TrainingDetail, TrainingProgress };
